import EntityLinkButton from '../../../../shared/components/linked-entities/EntityLinkButton';
import { entityLabel, selectedEntityIds } from '../../../../shared/utils/entitySelection';
import ExpenseDocuments from './ExpenseDocuments';
import '../../styles/expense-detail.css';
import { entitySelectionPayload } from '../../../../shared/components/EntityMultiSelect';
import {
  CreateFormDialog,
  CreateFormCancelButton,
  CreateFormSubmitButton,
} from '../../../../shared/components/CreateFormDialog';
import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, CheckCircle2, Clock, CreditCard, FileText, FolderOpen, Paperclip, RefreshCw, User, XCircle } from '../../../../shared/assets/AppIcons';
import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import { PrimaryActionButton, RowActions, StatusPill } from '../../../../shared/components/patterns';
import {
  formatAccountingCurrency,
  formatAccountingDate,
  getExpenseStatusMeta,
  getExpenseTypeMeta,
} from '../../utils';
import { getAccountingStatusTone } from '../shared/accountingPage.config';
import AccountingCommentsSection from '../shared/AccountingCommentsSection.jsx';
import ExpenseForm from './ExpenseForm.jsx';
import { useLinkedEntityCards } from '../../../../shared/components/linked-entities/LinkedEntityCardHost';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveAccountingCustomField,
  createAccountingCustomField,
  listAccountingCustomFields,
} from '../../services/accounting-custom-fields.service';

const EXPENSE_CUSTOM_FIELDS_API = {
  list: listAccountingCustomFields,
  create: createAccountingCustomField,
  archive: archiveAccountingCustomField,
};

function recurrenceLabel(expense) {
  if (!expense?.recurring || expense.recurring === 'una_tantum') return 'Una tantum';
  const label = String(expense.recurring).replaceAll('_', ' ');
  const next = expense.nextRecurrenceDueDate || expense.nextDueDate;
  const compactNext = next
    ? new Date(next).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
    : '';
  return `${label.charAt(0).toUpperCase() + label.slice(1)}${compactNext ? ` · prossima ${compactNext}` : ''}`;
}

function DetailSection({ title, children }) {
  return <section className="expense-detail__section" aria-label={title}><h3>{title}</h3>{children}</section>;
}

function DetailField({ label, icon: Icon, tone = '', hint, children }) {
  return <div className={`expense-detail__field${tone ? ` expense-detail__field--${tone}` : ''}`}>
    <span className="expense-detail__field-icon" aria-hidden="true"><Icon size={26} /></span>
    <div><dt>{label}</dt><dd className="select-text">{children}</dd>{hint ? <p className="expense-detail__hint">{hint}</p> : null}</div>
  </div>;
}

function beneficiaryEntries(expense, staff, suppliers, fallback) {
  return [['staff', staff, 'Personale'], ['supplier', suppliers, 'Fornitore']].flatMap(([kind, catalog, label]) => {
    const ids = selectedEntityIds(expense, kind);
    return [...new Set(ids)].map(id => ({
      entityType: kind, entityId: id,
      name: entityLabel(catalog.find(item => String(item.id) === id)) ||
        (ids.length === 1 ? (kind === 'supplier' ? expense.supplierName : '') || fallback : '') || `${label} ${id}`,
    }));
  });
}

function linkedEntries(expense) {
  const entries = [
    [expense.linkedEntityType, expense.linkedEntityId, expense.linkedEntityLabel || expense.originLabel],
    ['expense', expense.recurrenceParentId, 'Spesa ricorrente di origine'],
    ['assignment', expense.assignmentId, expense.assignmentTitle || 'Task collegato'],
    ['inProgressDeal', expense.inProgressDealId, 'Affare in corso'],
    ['completedDeal', expense.completedDealId, 'Affare completato'],
    ['invoice', expense.invoiceId, expense.invoiceNumber || 'Fattura collegata'],
  ].filter(([type, id]) => type && id != null && id !== '');
  return [...new Map(entries.map(([entityType, entityId, name]) => [
    `${entityType}:${entityId}`, { entityType, entityId, name: name || 'Apri collegamento' },
  ])).values()];
}

export default function ExpenseDetailDialog({
  open,
  initialMode = 'view',
  expense,
  recipientLabel,
  onClose,
  onRegisterPayment,
  onEditExpense,
  onVoidExpense,
  canManageAccountingDestructiveActions = false,
  focusComments = false,
  staff = [],
  suppliers = [],
  onUpdateExpense,
}) {
  const { openEntityCard } = useLinkedEntityCards();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const savePending = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  useEffect(() => {
    setEditing(initialMode === 'edit');
    setSaveError('');
  }, [open, expense?.id, initialMode]);
  const toDraft = (value) => ({
    description: value.description || '',
    type: value.type || 'spesa_aziendale',
    amount: String(value.amount ?? ''),
    dueDate: value.dueDate || '',
    ...entitySelectionPayload(value),
    recurrenceCount: value.recurrenceCount ?? '',
    recurring: value.recurring || 'una_tantum',
    recipientType: value.recipientType || 'OTHER',
    staffId: value.staffId || '',
    supplierId: value.supplierId || '',
    supplierName: value.supplierName || '',
    notes: value.notes || '',
    customFields: { ...(value.customFields || {}) },
  });
  useEffect(() => {
    if (expense) setDraft(toDraft(expense));
  }, [expense]);
  if (!expense) return null;

  if (editing && draft) {
    async function saveDraft() {
      if (savePending.current || !onUpdateExpense) return;
      savePending.current = true;
      setSaving(true);
      setSaveError('');
      try {
        const result = await onUpdateExpense(expense.id, draft);
        if (result?.ok === false) {
          setSaveError(result.message || 'Impossibile salvare la spesa.');
          return;
        }
        setEditing(false);
      } catch (error) {
        setSaveError(error.message || 'Impossibile salvare la spesa.');
      } finally {
        savePending.current = false;
        setSaving(false);
      }
    }
    return (
      <CreateFormDialog
        open={open}
        busy={saving}
        onOpenChange={(isOpen) => {
          if (!isOpen) setEditing(false);
        }}
        icon={FileText}
        title="Modifica spesa"
        className="accounting-form-dialog"
        footer={
          <>
            <CreateFormCancelButton disabled={saving} onClick={() => setEditing(false)} />
            <CreateFormSubmitButton form="expense-form" saving={saving} disabled={!onUpdateExpense}>
              Salva uscita
            </CreateFormSubmitButton>
          </>
        }
      >
        {saveError ? (
          <p role="alert" className="custom-fields-section__error wt-inline-error">
            {saveError}
          </p>
        ) : null}
        <ExpenseForm
          form={draft}
          staff={staff}
          suppliers={suppliers}
          isSubmitting={saving}
          enableCustomFields
          renderActions={false}
          onFieldChange={(field, value) => setDraft((current) => ({ ...current, [field]: value }))}
          onSubmit={saveDraft}
        />
      <AccountingCommentsSection entityType="expense" entityId={expense.id} />
      </CreateFormDialog>
    );
  }

  const type = getExpenseTypeMeta(expense.type);
  const status = getExpenseStatusMeta(expense.status);
  const canRegister = expense.status !== 'pagato' && expense.status !== 'annullata';
  const canVoid = canManageAccountingDestructiveActions && expense.status !== 'annullata';
  const isPaid = expense.status === 'pagato';
  const isVoided = expense.status === 'annullata';
  const isRecurring = Boolean(expense.recurring && expense.recurring !== 'una_tantum');
  const paymentTone = isPaid ? 'success' : expense.status === 'in_ritardo' ? 'warning' : isVoided ? 'neutral' : 'info';
  const paymentSummary = isPaid ? 'Saldata' : isVoided ? 'Annullata' : status.label;
  const paymentHint = isPaid
    ? `Importo saldato${expense.paymentDate ? ` · Pagata il ${formatAccountingDate(expense.paymentDate)}` : ''}`
    : isVoided ? 'Pagamento annullato' : 'Pagamento non registrato';
  const beneficiaryFallback = (recipientLabel && recipientLabel !== '-' ? recipientLabel : '') || expense.supplierName || '';
  const beneficiaries = beneficiaryEntries(expense, staff, suppliers, beneficiaryFallback);
  const connections = linkedEntries(expense);
  const links = entries => <div className="expense-detail__links">{entries.map(entry =>
    <EntityLinkButton key={`${entry.entityType}:${entry.entityId}`} onClick={() => openEntityCard(entry)}>{entry.name}</EntityLinkButton>
  )}</div>;

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={onClose}
      className="expense-detail accounting-detail--expense"
      avatarLabel={<CreditCard size={32} />}
      title={expense.description || type.label || 'Uscita'}
      headerMeta={<>
        <span className="expense-detail__eyebrow">Registro contabilità</span>
        <div className="expense-detail__status"><StatusPill label={isVoided ? 'Annullata' : status.label} tone={getAccountingStatusTone(expense.status)} /></div>
        <div className={`expense-detail__amount expense-detail__amount--${paymentTone}`}>
          <span>Importo</span><strong className="select-text">{formatAccountingCurrency(expense.amount)}</strong><small>{paymentSummary}</small>
        </div>
      </>}
      primaryActions={
        canRegister ? (
          <PrimaryActionButton iconKey="actions.accept" tone="success" onClick={() => onRegisterPayment?.(expense)}>
            Registra pagamento
          </PrimaryActionButton>
        ) : null
      }
      editActions={
        <RowActions
          actions={[
            {
              id: 'edit',
              iconKey: 'actions.edit',
              label: 'Modifica uscita',
              onClick: () => {
                setDraft(toDraft(expense));
                setSaveError('');
                setEditing(true);
                onEditExpense?.(expense);
              },
            },
          ]}
        />
      }
      dangerActions={
        canVoid ? (
          <RowActions
            actions={[
              {
                id: 'void',
                iconKey: 'actions.reject',
                label: 'Annulla uscita',
                onClick: () => onVoidExpense?.(expense),
                tone: 'danger',
              },
            ]}
          />
        ) : null
      }
    >
      <div className="expense-detail__layout">
        <DetailSection title="Descrizione">
          <p className="expense-detail__description select-text">{expense.notes || expense.note || expense.description || 'Nessuna descrizione'}</p>
        </DetailSection>
        <DetailSection title="Dettagli contabilità">
          <dl className="expense-detail__fields">
            <DetailField label="Scadenza" icon={CalendarDays}>{formatAccountingDate(expense.dueDate)}</DetailField>
            <DetailField label="Ricorrenza" icon={RefreshCw}
              hint={isRecurring ? `Rata ${expense.recurrenceNumber || 1} / ${expense.recurrenceCount || 'Senza limite'}` : ''}>
              {recurrenceLabel(expense)}
            </DetailField>
            <DetailField label="Beneficiario" icon={User} hint={
              expense.recipientType === 'STAFF' ? 'Personale' : expense.recipientType === 'SUPPLIER' ? 'Fornitore' : beneficiaries.length || beneficiaryFallback ? '' : 'Non specificato'
            }>{beneficiaries.length ? links(beneficiaries) : beneficiaryFallback || '—'}</DetailField>
            <DetailField label="Pagamento" icon={isPaid ? CheckCircle2 : isVoided ? XCircle : Clock} tone={paymentTone} hint={paymentHint}>
              {isVoided ? 'Annullato' : status.label}
            </DetailField>
            <DetailField label="Collegato a" icon={FolderOpen} hint={connections.length ? '' : 'Nessun collegamento'}>
              {connections.length ? links(connections) : '—'}
            </DetailField>
            <DetailField label="Documenti" icon={Paperclip}>
              <ExpenseDocuments key={expense.id} expense={expense} />
            </DetailField>
          </dl>
          <p className="expense-detail__type">Tipo di spesa: <strong>{type.label || 'Non specificato'}</strong></p>
        </DetailSection>
        <ClientCustomFieldsSection readOnly formKey="accounting.expense.create"
          values={expense.customFields || {}} api={EXPENSE_CUSTOM_FIELDS_API} />
        <section className="expense-detail__comments" aria-label="Commenti">
          <AccountingCommentsSection entityType="expense" entityId={expense.id} autoFocus={focusComments} />
        </section>
      </div>
    </EntityDetailDialog>
  );
}
